import { useMemo, useState } from 'react'
import { createRoute, redirect } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus, Tags, Trash2 } from 'lucide-react'
import { managementLayoutRoute } from '@/ui/pages/management'
import { useAuthStore } from '@/application/stores/auth.store'
import {
  SUPPORTED_LANG_CODES,
  resolveLanguage,
  type SupportedLangCode,
} from '@/application/i18n/language'
import { useDeleteManagedTerm, useGetManagedTerms } from '@/application/services/taxonomy.service'
import { handleApiError } from '@/infrastructure/api/client'
import { normalizeTermKey } from '@/domains/taxonomy/normalize-term'
import { termLabelIn, type ManagedTaxonomyTerm } from '@/domains/types'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/ui/components/common/Dialog'
import { Input } from '@/ui/components/forms/Input'
import { FilterableOptionList } from '@/ui/components/forms/FilterableOptionList'
import { CategoryDialog } from '@/ui/components/categories/CategoryDialog'

export const categoriesRoute = createRoute({
  getParentRoute: () => managementLayoutRoute,
  path: 'categories',
  beforeLoad: () => {
    const { user } = useAuthStore.getState()
    if (user?.role !== 'admin' && user?.role !== 'streamer') {
      throw redirect({ to: '/manage/profile' })
    }
  },
  component: CategoriesPage,
})

type SortKey = SupportedLangCode | 'usage'
type SortDirection = 'asc' | 'desc'

function compareTerms(
  left: ManagedTaxonomyTerm,
  right: ManagedTaxonomyTerm,
  sortKey: SortKey,
): number {
  if (sortKey === 'usage') return left.usageCount - right.usageCount
  const leftLabel = termLabelIn(left, sortKey)
  const rightLabel = termLabelIn(right, sortKey)
  return leftLabel.localeCompare(rightLabel, sortKey, { sensitivity: 'base' })
}

/** `null` while the dialog is closed; `{ term: null }` creates, `{ term }` edits. */
type DialogState = { term: ManagedTaxonomyTerm | null } | null

export function CategoriesPage() {
  const { t, i18n } = useTranslation()
  const { data: terms = [], isLoading } = useGetManagedTerms()
  const { mutateAsync: deleteTerm, isPending: isDeleting } = useDeleteManagedTerm()

  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'fr',
    direction: 'asc',
  })
  const [dialog, setDialog] = useState<DialogState>(null)
  const [deleteTarget, setDeleteTarget] = useState<ManagedTaxonomyTerm | null>(null)
  const [reassignTo, setReassignTo] = useState<string | undefined>(undefined)

  const visibleTerms = useMemo(() => {
    const filterKey = normalizeTermKey(filter)
    const matching = terms.filter(
      (term) =>
        filterKey.length === 0 ||
        SUPPORTED_LANG_CODES.some((locale) =>
          normalizeTermKey(termLabelIn(term, locale)).includes(filterKey),
        ),
    )
    const factor = sort.direction === 'asc' ? 1 : -1
    return [...matching].sort((left, right) => factor * compareTerms(left, right, sort.key))
  }, [terms, filter, sort])

  const toggleSort = (sortKey: SortKey) => {
    setSort((current) =>
      current.key === sortKey
        ? { key: sortKey, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: sortKey, direction: 'asc' },
    )
  }

  const closeDeleteDialog = () => {
    setDeleteTarget(null)
    setReassignTo(undefined)
  }

  const isDeleteTargetUsed = (deleteTarget?.usageCount ?? 0) > 0
  const uiLocale = resolveLanguage(i18n.language)
  const replacementOptions = terms
    .filter((term) => term.id !== deleteTarget?.id)
    .map((term) => ({ value: term.id, label: termLabelIn(term, uiLocale) || term.name }))

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteTerm({ termId: deleteTarget.id, reassignTo })
      toast.success(t('categories.delete_success'))
      closeDeleteDialog()
    } catch (error) {
      toast.error(handleApiError(error))
    }
  }

  const renderSortHeader = (sortKey: SortKey, label: string, className: string) => {
    const isActive = sort.key === sortKey
    const Icon = !isActive ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown
    return (
      <th
        className={className}
        aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => toggleSort(sortKey)}
          className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
        >
          {label}
          <Icon className="w-3.5 h-3.5" />
        </button>
      </th>
    )
  }

  const headerClass = 'text-left px-4 py-3'

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t('categories.filter_placeholder')}
          aria-label={t('categories.filter_placeholder')}
          className="sm:max-w-xs"
        />
        <Button
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setDialog({ term: null })}
        >
          {t('categories.add_button')}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_unused, index) => (
            <div key={index} className="h-14 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : visibleTerms.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Tags className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{t('categories.empty')}</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {SUPPORTED_LANG_CODES.map((locale) =>
                  renderSortHeader(locale, t(`categories.label_${locale}`), headerClass),
                )}
                {renderSortHeader('usage', t('categories.col_usage'), headerClass)}
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                  {t('categories.col_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleTerms.map((term) => (
                <tr key={term.id} className="hover:bg-muted/30 transition-colors">
                  {SUPPORTED_LANG_CODES.map((locale) => (
                    <td key={locale} className="px-4 py-3">
                      {termLabelIn(term, locale) || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-muted-foreground">{term.usageCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t('categories.edit_aria', { name: term.name })}
                        onClick={() => setDialog({ term })}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t('categories.delete_aria', { name: term.name })}
                        onClick={() => setDeleteTarget(term)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <CategoryDialog
          key={dialog.term?.id ?? 'new'}
          term={dialog.term}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null)
          }}
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('categories.delete_confirm')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{deleteTarget?.name}</p>
          {deleteTarget && isDeleteTargetUsed && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                {t('categories.delete_in_use', { count: deleteTarget.usageCount })}
              </p>
              <p className="text-sm font-medium">{t('categories.delete_reassign_label')}</p>
              <FilterableOptionList
                options={replacementOptions}
                value={reassignTo}
                onChange={setReassignTo}
                filterPlaceholder={t('categories.delete_reassign_filter')}
                emptyLabel={t('categories.delete_reassign_empty')}
                aria-label={t('categories.delete_reassign_label')}
              />
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t('common.cancel')}</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={isDeleteTargetUsed && !reassignTo}
              onClick={() => {
                void handleDelete()
              }}
              isLoading={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
